import React, { useEffect, useState, useCallback } from "react";
import { message, Modal, Button } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { getGadsOAuthRedirect } from "src/config/endpoints";
// Import the service
import { googleAdsService, CustomerAccount } from "src/services/GoogleAdsAPIs"; // Adjust the path as needed

const GADS_OAUTH_REDIRECT = getGadsOAuthRedirect();

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
}

const GoogleAdsModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConnected,
}) => {
  const tenant_id = localStorage.getItem("tenant_id");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTestLoading, setIsTestLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [refreshToken, setRefreshToken] = useState<string>("");
  const [accessToken, setAccessToken] = useState<string>("");
  const [customers, setCustomers] = useState<CustomerAccount[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [managerAccountId, setManagerAccountId] = useState<string>("");
  const [isCustomersLoading, setIsCustomersLoading] = useState<boolean>(false);
  const [hasFetchedCustomers, setHasFetchedCustomers] = useState<boolean>(
    false
  );
  const user_id = localStorage.getItem("user_id"); // Get this once here

  const fetchCustomers = useCallback(
    async (token: string) => {
      if (hasFetchedCustomers) {
        return;
      }
      setIsCustomersLoading(true);
      setErrorMessage("");
      try {
        const data = await googleAdsService.fetchCustomers(token);
        setCustomers(data.customer_accounts);
        if (data.customer_accounts && data.customer_accounts.length > 0) {
          // Safely access the optional property
          const firstCustomer = data.customer_accounts[0];
          if (firstCustomer.login_customer_id) {
            setManagerAccountId(firstCustomer.login_customer_id);
          } else {
            // Handle the case where it's missing, maybe set to the customer id?
            setManagerAccountId(firstCustomer.id);
            console.warn("login_customer_id not found, using id instead");
          }
        }
        setHasFetchedCustomers(true);
      } catch (error) {
        const err = error as Error;
        setErrorMessage(err.message || "Error fetching customers");
      } finally {
        setIsCustomersLoading(false);
      }
    },
    [hasFetchedCustomers]
  );
  useEffect(() => {
    const fetchExistingConnection = async () => {
      try {
        // Use the service function
        const result = await googleAdsService.fetchExistingConnection(
          tenant_id
        );
        if (result.status && result.gads_account_id) {
          setIsConnected(true);
          setSelectedCustomer(result.gads_account_id);
          setRefreshToken(result.gads_refresh_token);
          setAccessToken(result.gads_refresh_token);
          const customerOption = {
            id: result.gads_account_id,
            name: result.gads_account_id,
          };
          setCustomers([customerOption]);
          message.info(
            "Please sign in with a new account to change the connection."
          );
        }
      } catch (error) {
        const err = error as Error;
        console.error("Error fetching existing connection:", err);
        setErrorMessage(err.message || "Error fetching existing connection.");
      }
    };

    if (isOpen) {
      fetchExistingConnection();
    }
  }, [isOpen, tenant_id]); // api_key is no longer a dependency

  const signInWithGoogle = () => {
    const CLIENT_ID =
      "996633909190-2nnsu9ssi3kmjtkct4dsvt9e7hdgn1fj.apps.googleusercontent.com";
    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(
      GADS_OAUTH_REDIRECT
    )}&response_type=code&scope=https://www.googleapis.com/auth/adwords&access_type=offline&prompt=consent`;

    const handleOAuthMessage = (event: MessageEvent) => {
      if (event.data.refresh_token && event.data.access_token) {
        setRefreshToken(event.data.refresh_token);
        setAccessToken(event.data.access_token);
        fetchCustomers(event.data.access_token);
      }
    };

    // Remove any existing listeners before adding a new one
    window.removeEventListener("message", handleOAuthMessage);
    window.addEventListener("message", handleOAuthMessage);
    window.open(oauthUrl, "oauthWindow", "width=600,height=600");
  };

  const testConnection = async () => {
    if (!accessToken || !selectedCustomer) {
      setErrorMessage("Please sign in and select a customer");
      return;
    }
    if (!user_id) {
      setErrorMessage("User ID is required");
      return;
    }

    setIsTestLoading(true);
    setErrorMessage(""); // Clear previous errors
    try {
      // Use the service function
      await googleAdsService.testConnection({
        access_token: accessToken,
        customer_id: selectedCustomer,
        tenant_id: tenant_id,
        user_id: user_id,
        login_customer_id: managerAccountId,
      });
      message.success("Connection tested successfully!");
    } catch (error) {
      const err = error as Error;
      setErrorMessage(err.message || "Error testing connection");
    } finally {
      setIsTestLoading(false);
    }
  };

  const createConnection = async () => {
    if (!refreshToken || !selectedCustomer) {
      setErrorMessage("Please sign in and select a customer");
      return;
    }
    if (!user_id) {
      setErrorMessage("User ID is required");
      return;
    }

    setIsLoading(true);
    setErrorMessage(""); // Clear previous errors
    try {
      // Use the service function
      await googleAdsService.createConnection({
        refresh_token: refreshToken,
        customer_id: selectedCustomer,
        tenant_id: tenant_id,
        manager_account_id: managerAccountId,
        source: "google_ads",
        user_id: user_id,
        login_customer_id: managerAccountId,
      });
      message.success("Connection created successfully!");
      setIsConnected(true);
      onConnected();
    } catch (error) {
      const err = error as Error;
      setErrorMessage(err.message || "Error creating connection");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveConnection = async () => {
    setIsLoading(true);
    try {
      // Use the service function
      await googleAdsService.removeConnection(tenant_id);
      message.success("Connection removed successfully!");
      setIsConnected(false);
      setRefreshToken("");
      setAccessToken("");
      setSelectedCustomer("");
      setCustomers([]);
      setHasFetchedCustomers(false); // Reset this too
    } catch (error) {
      const err = error as Error;
      message.error(err.message || "Failed to remove connection");
    } finally {
      setIsLoading(false);
      setModalOpen(false);
    }
  };

  const handleChangeAccount = () => {
    setSelectedCustomer("");
    setRefreshToken("");
    setAccessToken("");
    setIsConnected(false);
    setCustomers([]);
    setHasFetchedCustomers(false);
    message.info("Please sign in with a new account.");
  };

 return (
  <>
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      className="google-ads-modal"
    >
      <div className="innerbody">
      {/*  {errorMessage && (
          <div className="error-message-source-connector">
            <span className="error-icon-source-connector">⚠️</span>
            <span>{errorMessage}</span>
          </div>
        )}*/}

        <img id="Google-Ads-Icon" src="/Images/google.png" alt="Google-Ads-Icon" />

        <div className="shopify-signin">
          <button
            type="button"
            onClick={signInWithGoogle}
            className="button button--primary"
            disabled={isConnected}
          >
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google logo"
              className="google-ads-sign-in"
            />
            Sign in with Google Ads
          </button>
        </div>

        {/* Access Token & Customer Fields */}
        <div className="form-input mt-3">
          <label htmlFor="accessToken">Access Token</label>
          <input
            type="password"
            id="accessToken"
            className="form-control"
            value={accessToken}
            readOnly
            disabled={isConnected}
          />
        </div>

        <div className="form-input mt-3">
          <label htmlFor="customer">Customer</label>
          <select
            id="customer"
            className="form-select"
            onChange={e => setSelectedCustomer(e.target.value)}
            disabled={isConnected || isCustomersLoading}
            value={selectedCustomer}
          >
            <option value="">
              {isCustomersLoading ? "Loading customers..." : "Select Customer"}
            </option>
            {!isCustomersLoading &&
              customers?.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.id}
                </option>
              ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="button-group mt-4">
          {isConnected ? (
            <>
              <Button
                className="button button--connection"
                onClick={() => setModalOpen(true)}
              >
                Remove Connection
              </Button>
              <Button className="button button--danger" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="button button--primary"
                onClick={handleChangeAccount}
              >
                Change Account
              </Button>
            </>
          ) : (
            <>
              <Button
                className="button button--connection"
                onClick={createConnection}
                loading={isLoading}
                icon={isLoading ? <SyncOutlined spin /> : null}
              >
                {isLoading ? "Creating..." : "Create Connection"}
              </Button>
              <Button className="button button--danger" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="button button--primary"
                onClick={testConnection}
                loading={isTestLoading}
                icon={isTestLoading ? <SyncOutlined spin /> : null}
              >
                {isTestLoading ? "Testing..." : "Test Connection"}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>

    {/* Nested Remove Confirmation */}
    <Modal
      className="logout-modal-content"
      centered
      open={modalOpen}
      footer={null}
      onCancel={() => setModalOpen(false)}
    >
      <div className="logout-modal-wrapper">
        <div className="logout-modal-header">
          <img
            src="/Icons/Logout Icon.svg"
            className="logout-Icon"
            alt="Logout Icon"
          />
          <span className="popup-msg-content">Remove Connection</span>
        </div>
        <div className="logout-modal-body">
          <span className="popup-msg-content">
            Removing your connection will remove all existing data. Continue?
          </span>
        </div>
        <div className="logout-modal-footer">
          <Button
            className="button button--connection"
            onClick={handleRemoveConnection}
            loading={isLoading}
            icon={isLoading ? <SyncOutlined spin /> : null}
          >
            {isLoading ? "Removing..." : "Continue"}
          </Button>
          <Button
            className="button button--danger"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  </>
);
};

export default GoogleAdsModal;
